<template>
  <div class="student-overview">
    <!-- Student Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card student-header">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <div class="avatar-wrapper mr-3">
                  <div class="avatar">
                    <img
                      v-if="student.student_info.avatar"
                      :src="student.student_info.avatar"
                      :alt="student.student_info.name"
                    />
                    <feather-icon v-else icon="UserIcon" size="24" />
                  </div>
                </div>
                <div>
                  <h4 class="mb-1">{{ student.student_info.name }}</h4>
                  <p class="text-muted mb-0">{{ student.student_info.email }}</p>
                </div>
              </div>
              <div>
                <b-button
                  variant="primary"
                  @click="$emit('view-details', student.student_info.id)"
                >
                  Подробнее
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Metrics -->
    <div class="row mb-4">
      <!-- Lessons Completed -->
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card metric-card lessons-completed">
          <div class="card-body text-center">
            <div class="metric-icon mb-3">
              <feather-icon icon="BookOpenIcon" size="32" class="text-success" />
            </div>
            <h3 class="metric-value text-success">{{ student.lessons_completed.total }}</h3>
            <h6 class="metric-label">Пройдено уроков</h6>
            <p class="metric-trend mb-0">
              <span class="text-muted">В этом месяце:</span>
              <span :class="getTrendClass(student.lessons_completed.trend)">
                {{ student.lessons_completed.this_month }}
                ({{ formatTrend(student.lessons_completed.trend) }})
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Missed Lessons -->
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card metric-card lessons-missed">
          <div class="card-body text-center">
            <div class="metric-icon mb-3">
              <feather-icon icon="XCircleIcon" size="32" class="text-warning" />
            </div>
            <h3 class="metric-value text-warning">{{ student.lessons_missed.total_missed }}</h3>
            <h6 class="metric-label">Пропущено уроков</h6>
            <p class="metric-trend mb-0">
              <span class="text-muted">Посещаемость:</span>
              <span :class="getAttendanceClass(student.lessons_missed.attendance_rate)">
                {{ student.lessons_missed.attendance_rate }}%
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- Homework Average -->
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card metric-card homework-grades">
          <div class="card-body text-center">
            <div class="metric-icon mb-3">
              <feather-icon icon="FileTextIcon" size="32" class="text-info" />
            </div>
            <h3 class="metric-value text-info">{{ student.homework_grades.average_score }}</h3>
            <h6 class="metric-label">Средняя оценка ДЗ</h6>
            <p class="metric-trend mb-0">
              <span class="text-muted">Проверено:</span>
              <span class="text-success">{{ student.homework_grades.total_graded }}/{{ student.homework_grades.total_assigned }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Lesson Limits -->
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card metric-card lesson-limits">
          <div class="card-body text-center">
            <div class="metric-icon mb-3">
              <feather-icon icon="CreditCardIcon" size="32" class="text-primary" />
            </div>
            <h3 class="metric-value text-primary">{{ student.lesson_limits.total_remaining }}</h3>
            <h6 class="metric-label">Осталось уроков</h6>
            <p class="metric-trend mb-0">
              <span class="text-muted">Использовано:</span>
              <span class="text-info">{{ student.lesson_limits.usage_percentage }}%</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Test Results & Recent Activity -->
    <div class="row">
      <!-- Test Results -->
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Результаты тестов</h5>
            <b-badge variant="primary">{{ student.test_results.total_tests }} тестов</b-badge>
          </div>
          <div class="card-body">
            <div class="test-summary mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span>Средний балл:</span>
                <span class="font-weight-bold text-success">{{ student.test_results.average_score }}%</span>
              </div>
              <b-progress
                :value="student.test_results.average_score"
                :variant="getProgressVariant(student.test_results.average_score)"
                class="mb-3"
              />
            </div>

            <!-- Subject Performance -->
            <div v-if="student.test_results.subject_performance && student.test_results.subject_performance.length">
              <h6 class="mb-2">По предметам:</h6>
              <div
                v-for="subject in student.test_results.subject_performance"
                :key="subject.subject"
                class="subject-performance mb-2"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-muted">{{ subject.subject }}</small>
                  <small class="text-muted">{{ subject.test_count }} тест{{ getTestWordEnding(subject.test_count) }}</small>
                </div>
                <b-progress
                  :value="subject.average_score"
                  :variant="getProgressVariant(subject.average_score)"
                  size="sm"
                />
              </div>
            </div>

            <!-- Recent Tests -->
            <div v-if="student.test_results.recent_tests && student.test_results.recent_tests.length">
              <h6 class="mt-3 mb-2">Последние тесты:</h6>
              <div class="recent-tests">
                <div
                  v-for="test in student.test_results.recent_tests.slice(0, 3)"
                  :key="test.test_title"
                  class="recent-test-item d-flex justify-content-between align-items-center py-1"
                >
                  <small class="text-truncate mr-2">{{ test.test_title }}</small>
                  <b-badge :variant="getGradeVariant(test.score)">{{ test.score }}%</b-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Activity -->
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Последняя активность</h5>
          </div>
          <div class="card-body">
            <div v-if="student.recent_activity && student.recent_activity.length" class="activity-list">
              <div
                v-for="(activity, index) in student.recent_activity.slice(0, 8)"
                :key="index"
                class="activity-item d-flex align-items-center py-2"
                :class="{ 'border-bottom': index < student.recent_activity.slice(0, 8).length - 1 }"
              >
                <div class="activity-icon mr-3">
                  <feather-icon
                    :icon="getActivityIcon(activity.type)"
                    size="16"
                    :class="getActivityIconClass(activity.type)"
                  />
                </div>
                <div class="flex-grow-1">
                  <p class="mb-0 font-weight-bold">{{ activity.title }}</p>
                  <small class="text-muted">{{ activity.component || activity.status }}</small>
                </div>
                <div class="activity-meta text-right">
                  <div v-if="activity.score !== undefined">
                    <b-badge :variant="getGradeVariant(activity.score)">{{ activity.score }}%</b-badge>
                  </div>
                  <small class="text-muted">{{ formatDate(activity.date) }}</small>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-3">
              <feather-icon icon="CalendarIcon" size="32" class="mb-2" />
              <p class="mb-0">Активность не найдена</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lesson Packages Details -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Пакеты уроков</h5>
            <b-badge
              :variant="student.lesson_limits.expiring_soon > 0 ? 'warning' : 'success'"
            >
              {{ student.lesson_limits.active_packages }} активн{{ getPackageWordEnding(student.lesson_limits.active_packages) }}
            </b-badge>
          </div>
          <div class="card-body">
            <div v-if="student.lesson_limits.active_packages > 0" class="lesson-packages">
              <div class="row mb-3">
                <div class="col-md-3">
                  <div class="package-stat text-center">
                    <h4 class="text-primary">{{ student.lesson_limits.total_paid }}</h4>
                    <small class="text-muted">Куплено уроков</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="package-stat text-center">
                    <h4 class="text-success">{{ student.lesson_limits.total_remaining }}</h4>
                    <small class="text-muted">Осталось уроков</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="package-stat text-center">
                    <h4 class="text-info">{{ student.lesson_limits.total_used }}</h4>
                    <small class="text-muted">Использовано уроков</small>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="package-stat text-center">
                    <h4 :class="student.lesson_limits.expiring_soon > 0 ? 'text-warning' : 'text-muted'">
                      {{ student.lesson_limits.expiring_soon }}
                    </h4>
                    <small class="text-muted">Истекают скоро</small>
                  </div>
                </div>
              </div>

              <!-- Usage Progress -->
              <div class="usage-progress mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span>Использование пакетов:</span>
                  <span class="font-weight-bold">{{ student.lesson_limits.usage_percentage }}%</span>
                </div>
                <b-progress
                  :value="student.lesson_limits.usage_percentage"
                  :variant="getUsageVariant(student.lesson_limits.usage_percentage)"
                />
              </div>

              <!-- Active Packages List -->
              <div v-if="student.lesson_limits.active_packages_list && student.lesson_limits.active_packages_list.length">
                <h6 class="mb-2">Активные пакеты:</h6>
                <div class="row">
                  <div
                    v-for="package_item in student.lesson_limits.active_packages_list"
                    :key="package_item.id"
                    class="col-md-6 mb-2"
                  >
                    <div class="package-item border rounded p-3">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-1">{{ package_item.name }}</h6>
                          <small class="text-muted">{{ package_item.remaining }}/{{ package_item.total }} уроков</small>
                        </div>
                        <div class="text-right">
                          <div v-if="package_item.expires_at">
                            <small class="text-muted">До {{ formatDate(package_item.expires_at) }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-3">
              <feather-icon icon="PackageIcon" size="32" class="mb-2" />
              <p class="mb-0">Нет активных пакетов уроков</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BBadge,
  BProgress,
  BCard,
  BCardHeader,
  BCardBody
} from 'bootstrap-vue'

export default {
  name: 'StudentOverview',
  components: {
    BButton,
    BBadge,
    BProgress,
    BCard,
    BCardHeader,
    BCardBody
  },
  props: {
    student: {
      type: Object,
      required: true
    }
  },
  methods: {
    formatTrend(trend) {
      if (trend > 0) return `+${trend}%`
      if (trend < 0) return `${trend}%`
      return '0%'
    },
    getTrendClass(trend) {
      if (trend > 0) return 'text-success'
      if (trend < 0) return 'text-danger'
      return 'text-muted'
    },
    getAttendanceClass(rate) {
      if (rate >= 90) return 'text-success'
      if (rate >= 70) return 'text-warning'
      return 'text-danger'
    },
    getProgressVariant(score) {
      if (score >= 90) return 'success'
      if (score >= 70) return 'info'
      if (score >= 50) return 'warning'
      return 'danger'
    },
    getGradeVariant(score) {
      if (score >= 90) return 'success'
      if (score >= 80) return 'primary'
      if (score >= 70) return 'info'
      if (score >= 60) return 'warning'
      return 'danger'
    },
    getUsageVariant(percentage) {
      if (percentage >= 90) return 'danger'
      if (percentage >= 70) return 'warning'
      return 'success'
    },
    getActivityIcon(type) {
      switch (type) {
        case 'lesson_completed': return 'CheckCircleIcon'
        case 'homework_submitted': return 'FileTextIcon'
        case 'test_completed': return 'AwardIcon'
        default: return 'ActivityIcon'
      }
    },
    getActivityIconClass(type) {
      switch (type) {
        case 'lesson_completed': return 'text-success'
        case 'homework_submitted': return 'text-info'
        case 'test_completed': return 'text-warning'
        default: return 'text-muted'
      }
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short'
      })
    },
    getTestWordEnding(count) {
      if (count === 1) return ''
      if (count >= 2 && count <= 4) return 'а'
      return 'ов'
    },
    getPackageWordEnding(count) {
      if (count === 1) return 'ый'
      if (count >= 2 && count <= 4) return 'ых'
      return 'ых'
    }
  }
}
</script>

<style lang="scss" scoped>
.student-overview {
  .student-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    .text-muted {
      color: rgba(255, 255, 255, 0.8) !important;
    }
  }

  .metric-card {
    border: none;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .metric-icon {
      opacity: 0.8;
    }

    .metric-value {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
    }

    .metric-label {
      color: #6c757d;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 0.8rem;
    }

    .metric-trend {
      font-size: 0.85rem;
    }
  }

  .avatar-wrapper {
    .avatar {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .activity-item {
    .activity-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .package-item {
    background: #f8f9fa;
    transition: background-color 0.3s ease;

    &:hover {
      background: #e9ecef;
    }
  }

  .package-stat {
    h4 {
      margin-bottom: 0.25rem;
    }
  }
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border-radius: 0.5rem;
}

.progress {
  height: 8px;
  border-radius: 4px;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.dark-layout {
  .student-overview {
    .student-header {
      background: linear-gradient(135deg, #5a67d8 0%, #667eea 100%);
    }

    .metric-card {
      background-color: #283046;
      color: #d0d2d6;

      .metric-label {
        color: #b4b7bd;
      }
    }

    .activity-item .activity-icon {
      background: #404656;
    }

    .package-item {
      background: #404656;
      border-color: #555 !important;

      &:hover {
        background: #4a5568;
      }
    }
  }

  .card {
    background-color: #283046;
    color: #d0d2d6;
  }

  .border-bottom {
    border-bottom-color: #404656 !important;
  }
}

@media (max-width: 768px) {
  .student-overview {
    .metric-card {
      margin-bottom: 1rem;

      .metric-value {
        font-size: 1.5rem;
      }
    }

    .package-stat h4 {
      font-size: 1.2rem;
    }
  }
}
</style>